import { useState } from 'react';
import Checkbox from 'components/checkbox';
import Switch from 'components/switch/Switch';
import { IconTeam } from 'components/teamSelect/IconTeam';
import { TEAM_PICKER_COPY, managerOfLabel } from 'constants/invites';
import { Team } from 'contexts/teams/types';
import SearchPicker from './SearchPicker';
import { SelectedTeam } from './types';
import {
  ManagerToggle,
  NoTeamsFound,
  TeamOption,
  TeamOptionLabel,
  TeamOptionList,
  TeamOptionName,
} from './styles';

interface ITeamPicker {
  teams: Team[];
  selectedTeams: SelectedTeam[];
  /** While the link is saving, the manager toggles show as pending like the other toggles. */
  saving?: boolean;
  onToggleTeam: (team: Team) => void;
  onToggleManager: (teamId: string) => void;
}

const PANEL_ID = 'invite-link-team-options';

// A searchable list of teams to tick, where each ticked team gets its own manager toggle.
const TeamPicker: React.FC<ITeamPicker> = ({
  teams,
  selectedTeams,
  saving,
  onToggleTeam,
  onToggleManager,
}) => {
  const [query, setQuery] = useState('');

  const search = query.trim().toLowerCase();
  const options = teams.filter(({ name }) => !search || name.toLowerCase().includes(search));
  const selectedById = new Map(selectedTeams.map((selected) => [selected.team.id, selected]));

  return (
    <SearchPicker
      panelId={PANEL_ID}
      label={TEAM_PICKER_COPY.LABEL}
      searchLabel={TEAM_PICKER_COPY.SEARCH_LABEL}
      placeholder={TEAM_PICKER_COPY.PLACEHOLDER}
      query={query}
      onQueryChange={setQuery}
    >
      {options.length === 0 ? (
        <NoTeamsFound>{TEAM_PICKER_COPY.NO_TEAMS}</NoTeamsFound>
      ) : (
        <TeamOptionList>
          {options.map((team) => {
            const selected = selectedById.get(team.id);
            return (
              <TeamOption key={team.id}>
                <Checkbox
                  aria-label={team.name}
                  checked={Boolean(selected)}
                  controlled
                  onChange={() => onToggleTeam(team)}
                  label={
                    <TeamOptionLabel>
                      {/* Decorative: the tick box is already named after the team. */}
                      <span aria-hidden="true">
                        <IconTeam
                          iconName={team.icon?.name}
                          color={team.icon?.color}
                          size={'smaller'}
                        />
                      </span>
                      <TeamOptionName>{team.name}</TeamOptionName>
                    </TeamOptionLabel>
                  }
                />
                {selected && (
                  <ManagerToggle>
                    <Switch
                      aria-label={managerOfLabel(team.name)}
                      checked={selected.is_manager}
                      loading={saving}
                      onChange={() => onToggleManager(team.id)}
                    />
                    <span>{TEAM_PICKER_COPY.MANAGER}</span>
                  </ManagerToggle>
                )}
              </TeamOption>
            );
          })}
        </TeamOptionList>
      )}
    </SearchPicker>
  );
};

export default TeamPicker;
